import tw, { styled } from 'twin.macro';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import L from 'leaflet';
import { MapContainer as Map, Marker, AttributionControl } from 'react-leaflet';
import VectorLayer from '../../../VectorLayer';
import { Heading, Code, LeadingAlt, Leading } from '../../../components/Typography';
import FIELDS_LIST from '../../upload/components/fieldsList';

if (typeof L.Icon !== 'undefined') {
  L.NumberedDivIcon = L.Icon.extend({
  	options: {
      iconUrl: '/img/marker-hole.png',
      number: '',
      shadowUrl: null,
      iconSize: new L.Point(25, 41),
  		iconAnchor: new L.Point(13, 41),
  		popupAnchor: new L.Point(0, -33),
  		className: 'leaflet-div-icon'
  	},

  	createIcon: function () {
  		var div = document.createElement('div');
  		var img = this._createImg(this.options['iconUrl']);
  		var numdiv = document.createElement('div');
  		numdiv.setAttribute ( "class", "number" );
  		numdiv.innerHTML = this.options['number'] || '';
  		div.appendChild ( img );
  		div.appendChild ( numdiv );
  		this._setIconStyles(div, 'icon');
  		return div;
  	},

  	createShadow: function () {
  		return null;
  	}
  });
}

const NoResultsWrapper = styled('div')`
  ${tw`flex justify-center items-center mx-auto mb-10`}

  i {
    ${tw`mr-3`}
  }
`

const NoResultsFooter = styled('div')`
  ${tw`mx-auto text-center -mt-8 text-lg mb-10`}
`

const ResultsWrapper = styled('div')`
  ${tw`mx-auto max-w-3xl mb-10`}

  .leaflet-container {
    ${tw`mx-auto w-full h-32`}
  }
`

const ResultsContainer = styled('div')`
  ${tw`flex flex-wrap`}
`

const Result = styled('div')`
  ${tw`w-1/2 mb-8`}
`

const Title = styled('h4')`
  ${tw`mb-0 mt-2 font-bold text-lg`}
`

const TitleLink = styled('a')`
  ${tw`ml-1 text-sm font-light`}
`

const ToggleButton = styled('button')`
  ${tw`bg-transparent border-none mr-1 px-0 hover:cursor-pointer`}
`

const DescriptionList = styled('dl')`
  ${tw`mt-0`}

  dt {
    ${tw`ml-0`}
  }

  dd {
    ${tw`ml-4`}
  }
`

class Visualize extends Component {

	static propTypes = {
		geocodingResult: PropTypes.object.isRequired,
		query: PropTypes.string,
		direction: PropTypes.string,
	};

	constructor() {
    super();

    this.state = {
      hiddenSections: []
    };
  }

  render() {
	  return (
	    <div ref={ref => this.resultsWrapperElement = ref}>
        {this.renderContents()}
      </div>
    )
  }

	renderContents() {
		if (this.props.geocodingResult && 'error' in this.props.geocodingResult) {
		  if (this.props.geocodingResult.error.indexOf('you have reached your current limits') === -1) {
		    return (
          <NoResultsWrapper>
            <i className="fa fa-question-circle fa-4x"></i>
            <Heading>{this.props.geocodingResult.error}</Heading>
          </NoResultsWrapper>
        );
      } else {
        return (
          <div>
            <NoResultsWrapper>
              <i className="fa fa-stop-circle fa-4x"></i>
              <Heading>You've reached the limit for this demo tool</Heading>
            </NoResultsWrapper>
            <NoResultsFooter>
              Please use our <a href="/upload/">spreadsheet upload tool</a> to geocode more addresses.
            </NoResultsFooter>
          </div>
        );
      }
		}

		return this.getFirstResult() ? this.renderResults() : (
		  <div>
        <NoResultsWrapper>
          <i className="fa fa-question-circle fa-4x"></i>
          <Heading>No results found</Heading>
        </NoResultsWrapper>
        <NoResultsFooter>
          Make sure that you entered a US or Canadian address.<br />
          Addresses in Canada needs to be suffixed with "Canada". <a href="/docs/#address-formats-2" target="_blank">Read more &raquo;</a>
        </NoResultsFooter>
      </div>
		);
	}

	componentDidMount() {
	  if (this.resultsWrapperElement) {
      window.scrollTo({
        top: this.resultsWrapperElement.getBoundingClientRect().top - 110,
        behavior: 'smooth'
      });
    }
  }

  getFirstResult() {
		return this.props.geocodingResult && this.props.geocodingResult.results && this.props.geocodingResult.results.length > 0 ? this.props.geocodingResult.results[0] : null;
	}

	renderResults = () => {
		return (
			<ResultsWrapper ref={ref => this.resultsWrapperElement = ref}>
        {this.renderMap()}

        <ResultsContainer>
  				{this.props.geocodingResult.results.map((result, index) => (
  					<Result key={index}>
  						<h3 tw="font-bold text-xl mt-4">Result #{index + 1}</h3>

              <Title>Location:</Title>
              <Code>{result.location.lat}, {result.location.lng}</Code>

  						<Title>Interpreted address:</Title>
  						{result.formatted_address}

  						<Title>Accuracy Type:</Title>
  						<a href="/guides/accuracy-types-scores/" target="_blank">{result.accuracy_type}</a>

  						<Title>Accuracy Score:</Title>
  						<a href="/guides/accuracy-types-scores/" target="_blank">{result.accuracy}</a>

  						<Title>Source:</Title>
  						{result.source}

              {result.fields && Object.keys(result.fields).map(name => this.renderField(name, result.fields[name]))}
  					</Result>
  				))}
  			</ResultsContainer>
      </ResultsWrapper>
		);
	}

	renderField = (name, result) => {
	  if (name === 'acs') {
	    return Object.keys(result)
        .filter(fieldName => fieldName !== 'meta')
        .map(fieldName => this.renderField(fieldName, result[fieldName]))
    }

	  const field = Object.values(FIELDS_LIST).flat().find(field => field.apiName === name);

	  const sectionVisible = this.state.hiddenSections.indexOf(field.name) === -1;

	  return (
	    <div key={name}>
        <Title>
          <ToggleButton onClick={() => this.toggleSection(field.name)}><i className={`fa ${sectionVisible ? 'fa-caret-down' : 'fa-caret-up'}`}></i></ToggleButton>
          {field.title}
          <TitleLink href={field.detailsUrl} target="_blank">What is this? <i className="fa fa-external-link-square"></i></TitleLink>
        </Title>
        {sectionVisible && this.renderJSON(result)}
      </div>
    );
  }

  toggleSection = fieldName => {
	  if (this.state.hiddenSections.indexOf(fieldName) === -1) {
      this.setState(prevState => ({
        hiddenSections: [
          ...prevState.hiddenSections,
          fieldName
        ]
      }));
    } else {
	    this.setState(prevState => ({
        hiddenSections: prevState.hiddenSections.filter(name => name !== fieldName)
      }));
	  }
  }

  urlify(text) {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex)
       .map(part => {
          if (part.match(urlRegex)) {
             return <a href={part} target="_blank">{part}</a>;
          }
          return part;
       });
  }

  renderJSON(obj) {
	  if (obj === null) {
	    return 'null';
    } else if (typeof obj === 'Array') {
	    return obj.map(this.renderJSON);
    } else if (typeof obj === 'object') {
	    return (
	      <DescriptionList>
          {Object.keys(obj).map(key => (
            <div key={key}>
              <dt>{this.formatKey(key)}:</dt>
              <dd>{this.renderJSON(obj[key])}</dd>
            </div>
          ))}
        </DescriptionList>
      )
    } else if (typeof obj === 'boolean') {
	    return (
	      <span key={obj}>
          {obj ? 'Yes' : 'No'}
        </span>
      )
    } else {
	    return (
	      <span key={obj}>
          {typeof obj === 'string' ? this.urlify(obj) : obj}
        </span>
      )
    }
  }

  formatKey(key) {
	  return _.upperFirst(key.replaceAll('_', ' '))
      .replace('Utc', 'UTC')
      .replace('Rss ', 'RSS ')
      .replace('Icpsr ', 'ICPSR ')
      .replace('Youtube', 'YouTube')
      .replace('Fips', 'FIPS');
  }

	renderMap = () => {
		const firstResult = this.getFirstResult();
		const position = [firstResult.location.lat, firstResult.location.lng];

		let zoom = 14;
		if (firstResult.accuracy_type === 'state') {
			zoom = 4;
		} else if (firstResult.accuracy_type === 'place') {
			zoom = 8;
		}

		return (
			<Map center={position} zoom={zoom} attributionControl={false}>
        <AttributionControl position="bottomright" prefix='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
				<VectorLayer />

				{this.props.direction === 'reverse' ? (<Marker position={this.props.query.split(',')} zIndexOffset={1001} />) : null}

				{this.props.geocodingResult.results.map((result, index) => (
					<Marker key={index} position={[result.location.lat, result.location.lng]} icon={new L.NumberedDivIcon({number: index + 1})} zIndexOffset={1000 - index} />
				))}
			</Map>
		);
	}
}

export default Visualize;
